/**
 * Talking to /api/bamiq from the browser.
 *
 * A turn streams back as server-sent events (api/bamiq.js): stage, emergency,
 * text, notice, done, error. They're read off the response body as they
 * arrive, so the coach sees the answer being written instead of waiting for
 * all of it. The last event, `done`, carries the checked answer, which
 * replaces what streamed.
 *
 * Every request carries the coach's session and says it comes from the web
 * (X-BAM-Client), which the server checks: BAM IQ is refused in the iPhone app
 * until a reviewed build turns it on (D106).
 */
import { supabase } from "../../supabaseClient";

export const PREVIEW = process.env.REACT_APP_BAMIQ_PREVIEW === "1";
const ENDPOINT = "/api/bamiq";

/** The local preview's synthetic sign-in (scripts/bamiq/previewServer.mjs): ?as=staff|coach|ios. */
export const previewPersona = () => {
  try { return new URLSearchParams(window.location.search).get("as") || "staff"; } catch { return "staff"; }
};

async function headers(json = true) {
  const h = { "X-BAM-Client": "web" };
  if (json) h["Content-Type"] = "application/json";
  if (PREVIEW) {
    const as = previewPersona();
    h.Authorization = `Bearer preview:${as}`;
    if (as === "ios") h["X-BAM-Client"] = "ios/2.4";
  } else {
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (session?.access_token) h.Authorization = `Bearer ${session.access_token}`;
  }
  return h;
}

async function asJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || "Something went wrong. Try again in a moment."), { status: res.status, code: body.code || null });
  return body;
}

export async function fetchOverview() {
  return asJson(await fetch(ENDPOINT, { headers: await headers(false) }));
}

export async function fetchThread(id) {
  return asJson(await fetch(`${ENDPOINT}?thread=${encodeURIComponent(id)}`, { headers: await headers(false) }));
}

export async function postAction(body) {
  return asJson(await fetch(ENDPOINT, { method: "POST", headers: await headers(), body: JSON.stringify(body) }));
}

/** Split an SSE text buffer into complete events; returns [events, rest]. */
export function parseSse(buffer) {
  const events = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop();
  for (const part of parts) {
    let type = "message";
    let data = "";
    for (const line of part.split("\n")) {
      if (line.startsWith("event: ")) type = line.slice(7).trim();
      else if (line.startsWith("data: ")) data += line.slice(6);
    }
    try { events.push({ type, ...(data ? JSON.parse(data) : {}) }); } catch { /* a malformed event is skipped */ }
  }
  return [events, rest];
}

/**
 * Send one message and stream the answer. `onEvent` gets each event; the
 * promise resolves with the `done` payload or rejects with a readable error.
 */
export async function streamTurn({ threadId, message, historyOff, attachments = [] }, onEvent, signal) {
  const res = await fetch(ENDPOINT, {
    method: "POST", signal,
    headers: { ...(await headers()), Accept: "text/event-stream" },
    body: JSON.stringify({ thread_id: threadId || null, message, history_off: !!historyOff, stream: true,
      // What the coach attached: type and id only. The server looks each one up itself.
      attachments: attachments.map(a => ({ type: a.type, id: a.id })) }),
  });
  if (!res.ok || !res.body) return asJson(res);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = null;
  let failure = null;
  let code = null;
  for (;;) {
    const { value, done: end } = await reader.read();
    if (end) break;
    buffer += decoder.decode(value, { stream: true });
    const [events, rest] = parseSse(buffer);
    buffer = rest;
    for (const ev of events) {
      if (ev.type === "done") done = ev;
      if (ev.type === "error") { failure = ev.error; code = ev.code || null; }
      onEvent(ev);
    }
  }
  if (done) return done;
  throw Object.assign(new Error(failure || "The answer was interrupted. Try again."), { code });
}

/** The founder's test cases (founderTools.js). Refused for any other account. */
export async function fetchCases() {
  return asJson(await fetch(`${ENDPOINT}?cases=1`, { headers: await headers(false) }));
}

/** The coach's own players, for "Attach a player's plan". */
export async function fetchPlayers() {
  return asJson(await fetch(`${ENDPOINT}?players=1`, { headers: await headers(false) }));
}

/** The coach's Save, Not now or Undo on a proposed plan change. The server's answer is the result. */
export async function executeProposal(body) {
  const res = await fetch("/api/bamiq-execute", { method: "POST", headers: await headers(), body: JSON.stringify(body) });
  const out = await res.json().catch(() => ({}));
  return { http: res.status, ...out };
}
